import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { connect } from 'react-redux';
import ThemeHOC from '@/connectHOCs/Theme';
import appWidgets from '@/utils/appWidgets';
import bridge from '@/utils/bridge';
import cssStyles from './Message.css';

function isValidDim(dim) {
  return dim && dim > 0 && `${dim}`.match(/^[0-9]+$/);
}

const DEFAULT_WIDGET_HEIGHT = '20px';
const DEFAULT_WIDGET_WIDTH = '100%';

// Hook for seamless iframe swap - keeps old content visible while new loads
function useIframeSwap(htmlContent) {
  const [slots, setSlots] = useState({ A: null, B: null });
  const [activeSlot, setActiveSlot] = useState('A');
  const loadingSlotRef = useRef(null);
  const prevHtmlRef = useRef(null);
  const urlsToRevokeRef = useRef([]);

  useEffect(() => {
    if (!htmlContent || prevHtmlRef.current === htmlContent) return undefined;
    prevHtmlRef.current = htmlContent;
    const url = URL.createObjectURL(
      new Blob([htmlContent], { type: 'text/html' })
    );
    urlsToRevokeRef.current.push(url);

    setSlots((s) => {
      if (!s.A) return { ...s, A: url };
      const next = activeSlot === 'A' ? 'B' : 'A';
      loadingSlotRef.current = next;
      return { ...s, [next]: url };
    });

    // Revoke old URLs (keep last 2)
    while (urlsToRevokeRef.current.length > 2) {
      URL.revokeObjectURL(urlsToRevokeRef.current.shift());
    }
    return undefined;
  }, [htmlContent, activeSlot]);

  // Cleanup all URLs on unmount
  useEffect(
    () => () => urlsToRevokeRef.current.forEach(URL.revokeObjectURL),
    []
  );

  const onLoad = useCallback((slot) => {
    if (loadingSlotRef.current === slot) {
      setActiveSlot(slot);
      loadingSlotRef.current = null;
    }
  }, []);

  return { slots, activeSlot, onLoad };
}

function getStyle(dynamicHeight, { height, width }) {
  const h = dynamicHeight || height;
  return {
    height: isValidDim(h) ? `${h}px` : DEFAULT_WIDGET_HEIGHT,
    width: isValidDim(width) ? `${width}px` : DEFAULT_WIDGET_WIDTH,
  };
}

function getFlockEvent(
  name,
  attachment,
  currentPeer,
  currentSessionOwner,
  messageId,
  messageSid
) {
  const { id: attachmentId = '' } = attachment;
  return {
    name,
    attachmentId,
    messageId: messageId || '',
    messageUid: messageSid || '',
    locale: 'en-us',
    ...appWidgets._genContextFlockEventProps(currentPeer, currentSessionOwner),
  };
}

function getUrlFromAttachmentObject(attachment) {
  if (attachment && attachment.views && attachment.views.widget) {
    return attachment.views.widget.src;
  }
  return '';
}

function getAppById(sessionId, appId, appList = []) {
  const app = appList.find(({ id }) => id === appId);
  if (app) {
    return Promise.resolve(app);
  }
  return bridge.ask('DikeController', 'getAppDataByAppId', [sessionId, appId]);
}

const useAppId = function (sessionId, appId, appList) {
  const [app, setApp] = useState(null);
  useEffect(() => {
    if (appId) {
      getAppById(sessionId, appId, appList).then((application) =>
        setApp(application)
      );
    }
  }, [appId, appList, sessionId]);
  return app;
};

const useContextParameters = function (
  app,
  claimToken,
  attachment,
  currentPeer,
  currentSessionOwner,
  messageId,
  messageSid
) {
  const appUrl = getUrlFromAttachmentObject(attachment);
  if (!attachment.appId) {
    return appUrl;
  }
  if (app?.eventToken && claimToken) {
    const flockEventParams = getFlockEvent(
      'client.openAttachmentWidget',
      attachment,
      currentPeer,
      currentSessionOwner,
      messageId,
      messageSid
    );
    return appWidgets.addContextParametersToUrl(
      appUrl,
      app?.eventToken,
      currentPeer,
      currentSessionOwner,
      'inline',
      flockEventParams,
      true,
      claimToken,
      false,
      app?.useFragmentParameters
    );
  }
  return null;
};

const InlineAttachment = React.memo(
  function InlineAttachment({
    claimToken,
    appList,
    attachment,
    currentSessionOwner,
    currentSessionId,
    currentPeer,
    height,
    messageId,
    messageSid,
  }) {
    const {
      appId,
      views: { html },
    } = attachment;
    const { inline } = html;
    const app = useAppId(currentSessionId, appId, appList);

    // Stable primitives to avoid re-renders from object reference changes
    const peerName = currentPeer?.name;
    const peerType = currentPeer?.type;
    const peerJid = currentPeer?.jid;
    const ownerName = currentSessionOwner?.name;
    const ownerJid = currentSessionOwner?.jid;
    const attachmentId = attachment?.id;
    const appEventToken = app?.eventToken;
    const htmlHeight = html?.height;
    const htmlWidth = html?.width;

    // Memoize the expensive DOM parsing and HTML generation
    // Only recalculate when actual values change, not object references
    // Use original objects for function calls to ensure compatibility
    const htmlContent = useMemo(() => {
      // Wait for app to load before generating final HTML
      // This prevents creating blob URL twice (once without app, once with)
      if (appId && !app) {
        return null;
      }

      const parser = new window.DOMParser();
      const inlineDoc = parser.parseFromString(inline, 'text/html');

      const flockEventParams = getFlockEvent(
        'client.openAttachmentWidget',
        attachment,
        currentPeer,
        currentSessionOwner,
        messageId,
        messageSid
      );
      const body = inlineDoc.getElementsByTagName('body')[0];
      if (body && app) {
        appWidgets.addContextParametersToDataset(
          body,
          appEventToken,
          currentPeer,
          currentSessionOwner,
          flockEventParams,
          claimToken
        );
      }
      return inlineDoc.documentElement.outerHTML;
    }, [
      app,
      appId,
      inline,
      peerName,
      peerType,
      peerJid,
      ownerName,
      ownerJid,
      appEventToken,
      messageId,
      messageSid,
      claimToken,
      attachmentId,
    ]);

    const { slots, activeSlot, onLoad } = useIframeSwap(htmlContent);

    // Memoize style calculation
    const style = useMemo(
      () => getStyle(height, { height: htmlHeight, width: htmlWidth }),
      [height, htmlHeight, htmlWidth]
    );

    const containerStyle = useMemo(
      () => ({
        position: 'relative',
        width: style.width,
        height: style.height,
      }),
      [style.width, style.height]
    );

    const styleA = useMemo(
      () => ({
        ...style,
        position: 'absolute',
        top: 0,
        left: 0,
        border: 'none',
        zIndex: activeSlot === 'A' ? 2 : 1,
        visibility: activeSlot === 'A' ? 'visible' : 'hidden',
        pointerEvents: activeSlot === 'A' ? 'auto' : 'none',
      }),
      [style, activeSlot]
    );

    const styleB = useMemo(
      () => ({
        ...style,
        position: 'absolute',
        top: 0,
        left: 0,
        border: 'none',
        zIndex: activeSlot === 'B' ? 2 : 1,
        visibility: activeSlot === 'B' ? 'visible' : 'hidden',
        pointerEvents: activeSlot === 'B' ? 'auto' : 'none',
      }),
      [style, activeSlot]
    );

    const onLoadA = useCallback(() => onLoad('A'), [onLoad]);
    const onLoadB = useCallback(() => onLoad('B'), [onLoad]);

    if (!slots.A) return null;

    return (
      <div style={containerStyle}>
        <iframe
          sandbox='allow-scripts allow-forms'
          src={slots.A}
          className={cssStyles.appViewIFrame}
          title='inline-iframe-a'
          style={styleA}
          onLoad={onLoadA}
        />
        {slots.B && (
          <iframe
            sandbox='allow-scripts allow-forms'
            src={slots.B}
            className={cssStyles.appViewIFrame}
            title='inline-iframe-b'
            style={styleB}
            onLoad={onLoadB}
          />
        )}
      </div>
    );
  },
  (prev, next) =>
    prev.attachment?.views?.html?.inline ===
      next.attachment?.views?.html?.inline && prev.height === next.height
);

const IframeAttachment = React.memo(function IframeAttachment({
  attachment,
  currentSessionOwner,
  currentSessionId,
  currentPeer,
  messageId,
  messageSid,
  appList,
  height,
  claimToken,
  keyOfIframe,
}) {
  const {
    views: { widget },
    appId,
  } = attachment;

  const app = useAppId(currentSessionId, appId, appList);
  const url = useContextParameters(
    app,
    claimToken,
    attachment,
    currentPeer,
    currentSessionOwner,
    messageId,
    messageSid
  );

  // Extract style values to make memoization effective
  const widgetHeight = widget?.height;
  const widgetWidth = widget?.width;

  // Memoize style calculation
  const style = useMemo(
    () => getStyle(height, { height: widgetHeight, width: widgetWidth }),
    [height, widgetHeight, widgetWidth]
  );

  // Track the last valid URL to prevent flickering during re-renders
  const lastUrlRef = useRef(url);
  if (url) {
    lastUrlRef.current = url;
  }

  const stableUrl = url || lastUrlRef.current;

  if (stableUrl) {
    return (
      <iframe
        sandbox='allow-same-origin allow-scripts allow-forms'
        className={cssStyles.appViewIFrame}
        title='widget_iframe'
        src={stableUrl}
        style={style}
        key={keyOfIframe}
      />
    );
  }
  return null;
});

class AttachmentWidget extends React.Component {
  constructor(props) {
    super(props);
    const {
      attachment: { appId },
    } = this.props;

    this.state = {
      height: null,
      keyOfIframe: 0,
      claimToken: appWidgets.generateClaimToken(appId),
    };
  }

  componentDidMount() {
    window.addEventListener('message', this.changeHeight);
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.changeHeight);
  }

  componentDidUpdate(prevProps) {
    const { currentTheme } = this.props;
    const prevTheme = prevProps.currentTheme;
    // For theme change, we need to re-render the iframe.
    if (currentTheme !== prevTheme) {
      this.setState(({ keyOfIframe }) => ({ keyOfIframe: keyOfIframe + 1 }));
    }
  }

  // changeHeight = ({ data: { claimToken, name, payload: { height } = {} } }) => {
  changeHeight = async (e) => {
    const { data } = e;
    const {
      claimToken: attachmentClaimToken,
      height: currentHeight,
    } = this.state;
    const { claimToken, name, payload } = data;
    if (
      name === 'resizeAttachmentView' &&
      claimToken === attachmentClaimToken
    ) {
      const { height } = payload;
      // Only update if height actually changed to prevent unnecessary re-renders
      if (height !== currentHeight) {
        this.setState({ height });
      }
    } else if (claimToken === attachmentClaimToken) {
      const {
        attachment,
        currentPeer,
        currentSessionId,
        currentSessionOwner,
        messageId,
        messageSid,
        appList,
      } = this.props;
      const { appId } = attachment;
      const app = await getAppById(currentSessionId, appId, appList);
      const flockEventParams = getFlockEvent(
        'client.openAttachmentWidget',
        attachment,
        currentPeer,
        currentSessionOwner,
        messageId,
        messageSid
      );
      const appData = {
        app,
        currentPeer,
        flockEventParams,
        sessionId: currentSessionId,
      };
      bridge.tell('Shell', 'handleAppRequest', [data, appData]);
    }
  };

  render() {
    const {
      isInline,
      appList,
      attachment,
      currentSessionOwner,
      currentSessionId,
      currentPeer,
      messageId,
      messageSid,
    } = this.props;
    const { height, claimToken, keyOfIframe } = this.state;
    if (!claimToken) return null;
    if (isInline) {
      return (
        <InlineAttachment
          attachment={attachment}
          currentSessionOwner={currentSessionOwner}
          currentSessionId={currentSessionId}
          currentPeer={currentPeer}
          messageId={messageId}
          messageSid={messageSid}
          appList={appList}
          claimToken={claimToken}
          height={height}
          keyOfIframe={keyOfIframe}
        />
      );
    }
    return (
      <IframeAttachment
        attachment={attachment}
        currentSessionId={currentSessionId}
        currentSessionOwner={currentSessionOwner}
        currentPeer={currentPeer}
        messageId={messageId}
        messageSid={messageSid}
        appList={appList}
        claimToken={claimToken}
        height={height}
        keyOfIframe={keyOfIframe}
      />
    );
  }
}

function mapStateToProps(state) {
  const ownerGuid = state.restricted._session.currentOwnerGuid;
  return {
    appList: state.restricted.dike[ownerGuid].appList,
  };
}

export default connect(mapStateToProps)(ThemeHOC(AttachmentWidget));
